/**
 * One JSON localStorage entry stamped with `savedAt` and valid for `ttlMs`.
 *
 * `validate(raw)` gets the stored (or to-be-stored) object and returns the
 * normalized fields to keep, or null when they are invalid. The stored payload
 * is those fields plus `savedAt`. Every storage access is wrapped: storage can
 * be unavailable (private mode, blocked site data) and then reads as empty.
 */
export function createExpiringStorageEntry(key, ttlMs, validate) {
  /**
   * Writes the entry and reads it back, since some browsers silently drop
   * writes. Returns whether it landed; invalid fields write nothing.
   */
  function persist(fields) {
    const value = validate(fields);
    if (!value) return false;
    try {
      const payload = JSON.stringify({ ...value, savedAt: Date.now() });
      localStorage.setItem(key, payload);
      return localStorage.getItem(key) === payload;
    } catch {
      return false;
    }
  }

  /**
   * The stored fields plus `savedAt`, or null when missing, malformed or (unless
   * `includeExpired`) older than ttlMs.
   */
  function read({ includeExpired = false } = {}) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      const value = validate(parsed);
      if (!value) return null;
      const { savedAt } = parsed;
      if (typeof savedAt !== "number" || !Number.isFinite(savedAt)) return null;
      if (!includeExpired && Date.now() - savedAt > ttlMs) return null;
      return { ...value, savedAt };
    } catch {
      return null;
    }
  }

  function clear() {
    try {
      localStorage.removeItem(key);
    } catch {
      // Nothing left to clear.
    }
  }

  return { persist, read, clear };
}

export function isNonEmptyString(value) {
  return typeof value === "string" && value.length > 0;
}
